import { useCallback, useEffect, useState } from "react";

// Plain-fetch API client for the verify-account endpoints (TIP-453).
// Self-contained inside this subtree — no Redux store, no RTK Query —
// mirroring the AgentIntake pattern. Auth = the activation_token in the
// URL; no JWT / session needed.

const API_BASE = "/api/lead-activation/lead";

async function apiRequest(path, options = {}) {
  const isFormData = options.body instanceof FormData;
  const headers = isFormData
    ? options.headers || {}
    : { "Content-Type": "application/json", ...(options.headers || {}) };

  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers,
    ...options,
  });

  let data = null;
  try {
    data = await response.json();
  } catch {
    /* no body */
  }

  if (!response.ok) {
    const err = new Error(`HTTP ${response.status}`);
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return data;
}

export function useVerifyLead(token, options = {}) {
  // `skip` lets callers (e.g. the Stripe-Checkout return flow at
  // VerifyAccountPage) bypass the lead GET entirely so it doesn't 410
  // with token_used when we're already in the post-activate state.
  const { skip = false } = options;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(Boolean(token) && !skip);

  const refetch = useCallback(async () => {
    if (!token || skip) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await apiRequest(`/${token}/`);
      setData(result);
    } catch (e) {
      setError(e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, skip]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, error, loading, refetch, setData };
}

// PATCH a single section. Body shape per TIP-453's verify_views:
//   { section: "<id>", data?: {...}, confirm?: true|false }
export async function patchVerifyLead(token, body) {
  return apiRequest(`/${token}/`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// Multipart license upload. `file` is a File / Blob.
export async function uploadLicense(token, file) {
  const fd = new FormData();
  fd.append("file", file);
  return apiRequest(`/${token}/license-upload/`, {
    method: "POST",
    body: fd,
  });
}

// Final activate — atomic User + Company + IndComp + auto-approved
// CompanyAdminRequests create flow. Backend rejects if all 6 confirms
// aren't true or password fails Django's validators.
export async function activateLead(token, password) {
  return apiRequest(`/${token}/activate/`, {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

// TIP-455 — post-activation Pay/Skip. Body shape:
//   { path: 'pay' | 'skip', plan?: 'monthly' | 'yearly' }
// Pay provisions VerifiedCompany + a placeholder VerifyCompanyPayment row;
// Skip just flips Company.subscription_status. Idempotent on token.
// (Note: the pay path is dead code once TIP-464 ships — PaymentScreen
// now goes via Stripe Embedded Checkout. Kept for skip-path use only.)
export async function finaliseLead(token, body) {
  return apiRequest(`/${token}/finalise/`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// TIP-464 — read-only snapshot of the post-activate state. Used by
// VerifyAccountPage when it detects ?stripe=cs_... in the URL after a
// Stripe Checkout redirect. Returns: { status, user, company } where
// company.subscription_status is 'accessed' until the webhook lands +
// flips it to 'verified_paid'. Caller polls until it transitions.
export async function fetchPostActivateState(token) {
  return apiRequest(`/${token}/post-activate-state/`, { method: 'GET' });
}

// TIP-464 — create a Stripe Embedded Checkout Session for the Verified
// subscription. Different endpoint family (/api/billing/) so this can't
// use the apiRequest helper (which prepends /api/lead-activation/lead).
// Body: { token, plan: 'monthly' | 'yearly' }. Returns { client_secret }.
export async function createVerifiedCheckoutSession({ companyID, token, plan }) {
  const body = {
    plan,
    ...(companyID && { company_id: companyID }),
    ...(token && { token }),
  };

  const response = await fetch('/api/billing/verified-checkout/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  let data = null;
  try { data = await response.json(); } catch { /* no body */ }
  if (!response.ok) {
    const err = new Error(`HTTP ${response.status}`);
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return data;
}

// Nominee — edit name / designation / phone. Email is OTP-gated below.
export async function patchNominee(token, body) {
  return apiRequest(`/${token}/nominee/`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// Email change — step 1: request OTP delivered to the new email.
export async function requestEmailOtp(token, newEmail) {
  return apiRequest(`/${token}/nominee/email-otp/request/`, {
    method: "POST",
    body: JSON.stringify({ new_email: newEmail }),
  });
}

// Email change — step 2: verify OTP; on success the backend swaps
// lead.nominee_email and returns the updated lead.
export async function verifyEmailOtp(token, otp) {
  return apiRequest(`/${token}/nominee/email-otp/verify/`, {
    method: "POST",
    body: JSON.stringify({ otp }),
  });
}

// Lightweight reference data — kept here so the subtree owns its own
// API surface end-to-end without reaching into shared utils.
export async function fetchSubIndustries() {
  const res = await fetch(`/api/company/sub-industry/`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`Sub-industry fetch failed: ${res.status}`);
  const json = await res.json();
  return json?.data || [];
}
